import { Suspense, lazy } from "react";
import { createBrowserRouter, createMemoryRouter } from "react-router-dom";
import App from "../App";
import {
  getEmbeddedAppInitialEntry,
  shouldUseHostedVuplexEmbeddedRouter,
} from "./vuplexRouter";
import {
  CheckoutRoute,
  MainBodyLoginRequiredRoute,
  RegisterRoute,
} from "./RouteUtils";
import { LoginRequiredRoute } from "./RouteUtils";
import ErrorPage from "../pages/ErrorPage";

const Authentication = lazy(() => import("../pages/Authentication"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const ProjectPage = lazy(() => import("../pages/ProjectPage"));
const Youtube = lazy(() => import("../pages/YoutubePlaylist").then((module) => ({ default: module.Youtube })));
const DownloadPage = lazy(() => import("../pages/DownloadPage"));
const LinkPage = lazy(() => import("../pages/Link"));
const LaunchPage = lazy(() => import("../pages/LaunchPage"));
const UserAccountSettings = lazy(() => import("../pages/UserAccountSettings"));
const Survey = lazy(() => import("../pages/Survey"));
const UserNameInput = lazy(() =>
  import("../pages/UserNameInput").then((module) => ({ default: module.UserNameInput }))
);
const ConfirmationScreen = lazy(() =>
  import("../pages/Authentication").then((module) => ({ default: module.ConfirmationScreen }))
);
const GoogleOAuthCallbackPage = lazy(() => import("../pages/authentication/GoogleOAuthCallbackPage"));


function LazyRouteElement({ children }) {
  return <Suspense fallback={<p> Loading...</p>}>{children}</Suspense>;
}

async function loadLandingRoute() {
  const module = await import("../pages/Dashboard");
  return {
    loader: module.loader,
  };
}

async function loadProjectPageRoute() {
  const module = await import("../pages/ProjectPage");
  return {
    loader: module.loader,
    shouldRevalidate: module.shouldRevalidate,
  };
}

function throwNotFoundResponse() {
  throw new Response("The requested page could not be found.", {
    status: 404,
    statusText: "Not Found",
  });
}

const routeDefinitions = [
  {
    path: "/",
    element: <App />,
    handle: { name: "app-root" },
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <MainBodyLoginRequiredRoute
            element={
                <LazyRouteElement>
                <Dashboard />
              </LazyRouteElement>
            }
          />
        ),
        lazy: loadLandingRoute,
        action: async ({ request }) => {
          const module = await import("../pages/Dashboard");
          return module.action({ request });
        },
        handle: { name: "landing-page" },
      },
      {
        path: "/:id",
        element: (
          <MainBodyLoginRequiredRoute
            element={(
              <LazyRouteElement>
                <ProjectPage />
              </LazyRouteElement>
            )}
          />
        ),
        lazy: loadProjectPageRoute,
        handle: { name: "project-details" },
      },
      {
        path: "help",
        element: (
          <MainBodyLoginRequiredRoute
            element={
              <LazyRouteElement>
                <Youtube />
              </LazyRouteElement>
            }
          />
        ),
        handle: { name: "help" },
      },
      {
        path: "download",
        element: (
          <LazyRouteElement>
            <DownloadPage />
          </LazyRouteElement>
        ),
        handle: { name: "download" },
      },
      {
        path: "checkout",
        element: <CheckoutRoute />,
        handle: { name: "checkout" },
      },
      {
        path: "link/:sessionId",
        element: (
          <LazyRouteElement>
            <LinkPage />
          </LazyRouteElement>
        ),
        handle: { name: "session-link" },
      },
      {
        path: "confirm",
        element: (
          <LazyRouteElement>
            <ConfirmationScreen />
          </LazyRouteElement>
        ),
        handle: { name: "confirm" },
      },
      {
        path: "login",
        element: (
          <LoginRequiredRoute
            element={
              <LazyRouteElement>
                <Authentication />
              </LazyRouteElement>
            }
          />
        ),
        handle: { name: "login" },
      },
      {
        path: "forgot-password",
        element: (
          <LazyRouteElement>
            <Authentication Screen={"forgotPassword"} />
          </LazyRouteElement>
        ),
        handle: { name: "forgot-password" },
      },
      {
        path: "confirm-reset-password",
        element: (
          <LazyRouteElement>
            <Authentication Screen={"forgotPassword"} />
          </LazyRouteElement>
        ),
        handle: { name: "confirm-reset-password" },
      },
      {
        path: "settings",
        element: (
          <LoginRequiredRoute
            element={
              <LazyRouteElement>
                <UserAccountSettings />
              </LazyRouteElement>
            }
          />
        ),
        handle: { name: "settings" },
      },
      {
        path: "launch-animotive",
        element: (
          <LoginRequiredRoute
            element={
              <LazyRouteElement>
                <LaunchPage />
              </LazyRouteElement>
            }
          />
        ),
        handle: { name: "launch-animotive" },
      },
      {
        path: "survey",
        element: (
          <LoginRequiredRoute
            element={
              <LazyRouteElement>
                <Survey />
              </LazyRouteElement>
            }
          />
        ),
        handle: { name: "survey" },
      },
      {
        path: "signup-completion",
        element: (
          <LoginRequiredRoute
            element={
              <LazyRouteElement>
                <UserNameInput />
              </LazyRouteElement>
            }
          />
        ),
        handle: { name: "signup-completion" },
      },
      {
        path: "register",
        element: <RegisterRoute />,
        handle: { name: "register" },
      },
      {
        path: "oauth-callback",
        element: (
          <LazyRouteElement>
            <GoogleOAuthCallbackPage />
          </LazyRouteElement>
        ),
        handle: { name: "oauth-callback" },
      },
      {
        path: "*",
        loader: throwNotFoundResponse,
        handle: { name: "not-found" },
      },
    ],
  },
];

export function createAppRouter({
  useEmbeddedRouter = shouldUseHostedVuplexEmbeddedRouter(),
  initialEntries,
} = {}) {
  if (useEmbeddedRouter) {
    return createMemoryRouter(routeDefinitions, {
      initialEntries: initialEntries ?? [getEmbeddedAppInitialEntry()],
    });
  }

  return createBrowserRouter(routeDefinitions);
}

const router = createAppRouter();

export default router;
export { shouldUseHostedVuplexEmbeddedRouter };
